import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { getPersonIdFromToken, getPreferredUsernameFromToken, saveAllAccounts, saveRegisteredDevice, useAccount, } from '@ibk/shared-auth';
import { clearAllProducts, clearAllVouchers, clearBillingProducts, clearHasAccessData, clearPendingDomains, clearPermissioningCache, clearTokens, clearUser, config, persistor, PersonDocumentType, resetAllApiCaches, setHasAccessData, setTokens, useAppDispatch, useGetProductRelationShips, } from '@ibk/shared-config';
import { localStorageService, StorageKeys } from '@ibk/shared-utils';
import { getBpoCompanies } from './Bpo';
import { BPO_OPERATIONS, DOMAIN_RECEIPT_KEY, getDomainOrigin, isStandaloneBpo } from './bpoDomains';
function validatePayload(payload) {
    if (typeof payload.accessToken !== 'string' ||
        typeof payload.deviceId !== 'string' ||
        !payload.deviceId ||
        (payload.refreshToken !== undefined && typeof payload.refreshToken !== 'string'))
        throw new Error('Invalid session');
    if (!isStandaloneBpo() &&
        (typeof payload.companyId !== 'string' || !payload.operation || !Object.hasOwn(BPO_OPERATIONS, payload.operation)))
        throw new Error('Invalid operation');
}
async function validateSession(payload) {
    validatePayload(payload);
    const response = await fetch(`${config.apiBaseUrl}/v1/person-accesses?requires-account=false`, {
        headers: {
            Authorization: `Bearer ${payload.accessToken}`,
            'device-id': payload.deviceId,
            'X-Application-Name': 'internet-banking-client',
        },
        signal: AbortSignal.timeout(20000),
    });
    if (!response.ok)
        throw new Error('Session rejected');
    const body = (await response.json());
    if (!Array.isArray(body.result))
        throw new Error('Invalid access response');
    if (!isStandaloneBpo() && !getBpoCompanies(body.result).some(c => c.targetId === payload.companyId))
        throw new Error('Company access revoked');
    const personId = getPersonIdFromToken(payload.accessToken);
    if (!personId)
        throw new Error('Invalid identity');
    const documentNumber = getPreferredUsernameFromToken(payload.accessToken)?.replace(/[.-]/g, '');
    if (!documentNumber || !/^\d{11}$/.test(documentNumber))
        throw new Error('Invalid document');
    const params = new URLSearchParams({ documentType: String(PersonDocumentType.CPF), documentNumber });
    const identityResponse = await fetch(`${config.apiBaseUrl}/v1/onboarding/has-access?${params}`, {
        headers: {
            Authorization: `Bearer ${payload.accessToken}`,
            'device-id': payload.deviceId,
            'X-Application-Name': 'internet-banking-client',
        },
        signal: AbortSignal.timeout(20000),
    });
    if (!identityResponse.ok)
        throw new Error('Identity unavailable');
    const identityBody = (await identityResponse.json());
    const identity = Array.isArray(identityBody.result) ? identityBody.result[0] : undefined;
    if (identity?.has_access !== true ||
        typeof identity.full_name !== 'string' ||
        !identity.full_name.trim() ||
        (identity.document !== undefined && identity.document.replace(/\D/g, '') !== documentNumber))
        throw new Error('Invalid identity response');
    return { personId, accesses: body.result, identity, documentNumber };
}
export default function DomainSessionReceiver() {
    const dispatch = useAppDispatch();
    const { getAccounts, updateSelectedAccount } = useAccount();
    const { saveBillingProduct } = useGetProductRelationShips();
    const handlers = useRef({ getAccounts, updateSelectedAccount, saveBillingProduct });
    handlers.current = { getAccounts, updateSelectedAccount, saveBillingProduct };
    useEffect(() => {
        function clearDestinationSession() {
            localStorageService.removeItem(StorageKeys.AuthToken);
            localStorageService.removeItem(StorageKeys.AuthRefreshToken);
            localStorageService.removeItem(StorageKeys.AllAccounts);
            localStorageService.removeItem(StorageKeys.AccountId);
            localStorageService.removeItem(StorageKeys.LastAccessedCompany);
            localStorageService.removeItem(StorageKeys.BillingProduct);
            localStorageService.removeItem(StorageKeys.BillingProductNFSe);
            localStorageService.removeItem(StorageKeys.BillingProductRegua);
            localStorage.removeItem(DOMAIN_RECEIPT_KEY);
            dispatch(clearTokens());
            dispatch(clearUser());
            dispatch(clearHasAccessData());
            dispatch(clearPermissioningCache());
            dispatch(clearPendingDomains());
            dispatch(clearAllProducts());
            dispatch(clearBillingProducts());
            dispatch(clearAllVouchers());
            resetAllApiCaches(dispatch);
        }
        if (globalThis.parent === globalThis.self)
            return;
        const origin = getDomainOrigin(isStandaloneBpo() ? 'empresa' : 'bpo');
        const nonce = globalThis.location.hash.slice(1);
        globalThis.history.replaceState(null, '', '/session-transfer');
        if (!/^[a-f0-9-]{36}$/.test(nonce))
            return;
        let accepted = false;
        const receive = async (event) => {
            const payload = event.data;
            if (accepted ||
                event.origin !== origin ||
                event.source !== globalThis.parent ||
                payload?.type !== 'ibk-domain-session' ||
                payload.nonce !== nonce)
                return;
            accepted = true;
            let installed = false;
            try {
                const { personId, accesses, identity, documentNumber } = await validateSession(payload);
                installed = true;
                clearDestinationSession();
                await saveRegisteredDevice(personId, payload.deviceId, true);
                localStorageService.setItemSync(StorageKeys.AuthToken, payload.accessToken);
                if (payload.refreshToken)
                    localStorageService.setItemSync(StorageKeys.AuthRefreshToken, payload.refreshToken);
                else
                    localStorageService.removeItem(StorageKeys.AuthRefreshToken);
                dispatch(setTokens({ accessToken: payload.accessToken, refreshToken: payload.refreshToken }));
                dispatch(setHasAccessData({ data: identity, documentNumber }));
                saveAllAccounts(accesses);
                if (!isStandaloneBpo()) {
                    const account = handlers.current.getAccounts().find(c => c.id === payload.companyId);
                    if (!account)
                        throw new Error('Company unavailable');
                    handlers.current.updateSelectedAccount(account);
                    await handlers.current.saveBillingProduct(account.id);
                }
                await persistor.flush();
                localStorage.setItem(DOMAIN_RECEIPT_KEY, JSON.stringify({
                    nonce,
                    personId,
                    deviceId: payload.deviceId,
                    expiresAt: Date.now() + 30000,
                    fromBpo: !isStandaloneBpo(),
                }));
                globalThis.parent.postMessage({ type: 'ibk-domain-complete', nonce }, origin);
            }
            catch {
                if (installed) {
                    clearDestinationSession();
                    await persistor.flush().catch(() => undefined);
                }
                globalThis.parent.postMessage({ type: 'ibk-domain-failed', nonce }, origin);
            }
        };
        globalThis.addEventListener('message', receive);
        globalThis.parent.postMessage({ type: 'ibk-domain-ready', nonce }, origin);
        return () => globalThis.removeEventListener('message', receive);
    }, [dispatch]);
    return _jsx("div", {});
}
